import { AG_PROD_DEFAULT, AG_PROD_SEED, AG_PROD_BANDS, EQUIPO_TIC } from "./constants";

// ── Helpers generales ─────────────────────────────────────────────────────────
export const shortName = (name="") => { const p=name.trim().split(" ").filter(Boolean); return p.length===1?p[0]:p[0]+" "+p[1][0]+"."; };
// Nombre corto para sitios estrechos: nombre de pila completo e inicial del primer
// apellido ("ANTONIO GIMENEZ PEREZ" -> "Antonio G."). Acepta un usuario ({name}) o
// el nombre suelto; el nombre completo va aparte, en title y aria-label. Los
// nombres de Odoo llegan en mayusculas, de ahi el Title Case.
export const nombreCorto = (u) => {
  const nombre = typeof u === "string" ? u : (u?.name || u?.nombre || "");
  const p = nombre.trim().split(/\s+/).filter(Boolean)
    .map(w => w.charAt(0).toLocaleUpperCase("es") + w.slice(1).toLocaleLowerCase("es"));
  if (!p.length) return "";
  return p.length === 1 ? p[0] : p[0] + " " + p[1].charAt(0) + ".";
};
export const uid      = () => Math.random().toString(36).slice(2,10);
export const fmt      = d => { if(!d||d==="null"||d==="undefined") return "—"; try { const s=String(d).slice(0,10); if(!s.match(/^\d{4}-\d{2}-\d{2}$/)) return "—"; const [y,m,day]=s.split("-"); return new Date(parseInt(y),parseInt(m)-1,parseInt(day)).toLocaleDateString("es-ES",{day:"2-digit",month:"short"}); } catch { return "—"; } };
export const fmtFull  = d => { if(!d||d==="null"||d==="undefined") return "—"; try { const s=String(d).slice(0,10); if(!s.match(/^\d{4}-\d{2}-\d{2}$/)) return "—"; const [y,m,day]=s.split("-"); return new Date(parseInt(y),parseInt(m)-1,parseInt(day)).toLocaleDateString("es-ES",{day:"2-digit",month:"short",year:"numeric"}); } catch { return "—"; } };
export const today    = () => new Date().toISOString().slice(0,10);

// ── Días laborables ───────────────────────────────────────────────────────────
// El calendario laboral marca los festivos; el fin de semana es implícito.
export const esFinde = iso => {
  if (!iso) return false;
  const d = new Date(String(iso).slice(0,10) + "T00:00:00");
  return isNaN(d) ? false : (d.getDay() === 0 || d.getDay() === 6);
};

// { "2026-01-01": "Año Nuevo", ... } a partir de los eventos tipo 'festivo'.
// Un festivo con fecha_fin (p. ej. un puente) ocupa todos los días del rango.
export const buildFestivos = (eventos=[]) => {
  const map = {};
  eventos.forEach(e => {
    if (e.tipo !== "festivo") return;
    const ini = String(e.fecha || "").slice(0,10);
    if (!ini) return;
    const fin = e.fecha_fin ? String(e.fecha_fin).slice(0,10) : ini;
    const d = new Date(ini + "T00:00:00");
    const end = new Date(fin + "T00:00:00");
    if (isNaN(d) || isNaN(end)) return;
    // Tope de seguridad: un rango absurdo no debe colgar la interfaz
    for (let i = 0; d <= end && i < 400; i++) {
      // Formato local: toISOString() restaría un día en husos al este de UTC
      const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      if (!map[iso]) map[iso] = e.titulo || "Festivo";
      d.setDate(d.getDate() + 1);
    }
  });
  return map;
};

export const esFestivo     = (iso, festivos={}) => !!festivos[String(iso||"").slice(0,10)];
export const esNoLaborable = (iso, festivos={}) => esFinde(iso) || esFestivo(iso, festivos);

// Días del mes que no son ni fin de semana ni festivo (month: 0-11)
export const diasLaborables = (year, month, festivos={}) => {
  const total = new Date(year, month + 1, 0).getDate();
  let n = 0;
  for (let d = 1; d <= total; d++) {
    const iso = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    if (!esNoLaborable(iso, festivos)) n++;
  }
  return n;
};

// ── Semanas y personas (guardias y vacaciones) ────────────────────────────────
// Formato local: toISOString() restaría un día en husos al este de UTC
export const isoLocal = d =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

// Lunes de la semana a la que pertenece una fecha ISO. Es la clave con la que se
// identifica una semana en todo el sistema de guardias.
export const lunesDe = iso => {
  const d = new Date(String(iso).slice(0,10) + "T00:00:00");
  if (isNaN(d)) return null;
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return isoLocal(d);
};

export const sumarDias = (iso, n) => {
  const d = new Date(String(iso).slice(0,10) + "T00:00:00");
  d.setDate(d.getDate() + n);
  return isoLocal(d);
};

// Nº de semana ISO: la semana 1 es la que contiene el primer jueves del año
export const numeroSemana = iso => {
  const d = new Date(String(iso).slice(0,10) + "T00:00:00");
  if (isNaN(d)) return 0;
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + 3);   // jueves de esa semana
  const primerJueves = new Date(d.getFullYear(), 0, 4);
  primerJueves.setDate(primerJueves.getDate() - ((primerJueves.getDay() + 6) % 7) + 3);
  return 1 + Math.round((d - primerJueves) / (7 * 86400000));
};

// Semanas de un año: aquellas cuyo LUNES cae dentro del año.
//
// La semana a caballo entre dos años pertenece al año en que empieza, no al que
// termina. Si no, el cuadrante de enero arrancaría con una semana que ya está
// asignada en el año anterior: aparecería como cubierta, contaría en el reparto
// y el mismo turno saldría en dos años. Los días de enero que cubre esa semana
// se siguen pintando, porque la vista incluye también la semana anterior.
export const semanasDelAnio = year => {
  const out = [];
  let cursor = lunesDe(`${year}-01-01`);
  if (cursor < `${year}-01-01`) cursor = sumarDias(cursor, 7);
  while (cursor <= `${year}-12-31`) {
    out.push(cursor);
    cursor = sumarDias(cursor, 7);
  }
  return out;
};

// ── Tramo real de una guardia ─────────────────────────────────────────────────
// El relevo se hace en persona, así que solo puede ocurrir en día laborable. Si
// el lunes es festivo, quien estaba de guardia sigue cubriendo hasta el primer
// día hábil: su tramo dura un día más y el del siguiente, uno menos.

// Primer día hábil a partir de una fecha (incluida): ni fin de semana ni festivo
export const primerLaborable = (iso, festivos = {}) => {
  let d = String(iso).slice(0, 10);
  // Tope de 10 días: cubre cualquier puente real sin poder colgarse
  for (let i = 0; i < 10; i++) {
    if (!esFinde(d) && !festivos[d]) return d;
    d = sumarDias(d, 1);
  }
  return d;
};

// Tramo efectivo de la guardia cuya semana empieza en `lunes`:
// desde su primer día hábil hasta el día anterior al primer día hábil de la
// semana siguiente. Así los tramos encadenan sin huecos ni solapes.
export const tramoGuardia = (lunes, festivos = {}) => {
  const inicio = primerLaborable(lunes, festivos);
  const fin    = sumarDias(primerLaborable(sumarDias(lunes, 7), festivos), -1);
  return { inicio, fin, dias: Math.round((new Date(fin) - new Date(inicio)) / 86400000) + 1 };
};

// Festivos que caen dentro del tramo real de una guardia. Solo cuentan los de
// día laborable: uno en sábado no añade carga, ese día ya estaba cubierto.
export const festivosDeTramo = (lunes, festivos = {}) => {
  const t = tramoGuardia(lunes, festivos);
  const out = [];
  let d = t.inicio;
  for (let i = 0; d <= t.fin && i < 20; i++) {
    if (festivos[d] && !esFinde(d)) out.push({ fecha: d, nombre: festivos[d] });
    d = sumarDias(d, 1);
  }
  return out;
};

// ── Planificador del cuadrante de guardias ────────────────────────────────────
// Reparte las semanas persiguiendo tres cosas a la vez:
//   1. que nadie coja guardia estando de vacaciones,
//   2. que el número de semanas quede parejo,
//   3. que el número de festivos quede parejo — que es lo que de verdad pesa,
//      porque son los que se pagan doble.
//
// Se resuelve con un voraz semana a semana en vez de con una optimización
// global: es predecible, se explica en una frase y el resultado es lo bastante
// bueno. La clave es tratar distinto las semanas con festivo y sin él.
//
// Vive en el cliente a propósito: los festivos y los tramos ya se calculan aquí
// para pintar el calendario, y duplicar esa lógica en SQL nos llevaría a que las
// dos versiones se separaran con el tiempo.
export function planificarGuardias({
  semanas = [], participantes = [], festivos = {}, vacaciones = [],
  asignadas = {}, sobrescribir = true, equilibrarFestivos = true, previo = null,
}) {
  const ids = participantes.map(String).filter(Boolean);
  if (!ids.length || !semanas.length) return { asignaciones: [], respetadas: 0, saltadas: 0, sinCubrir: 0 };

  const ausencias = {};
  vacaciones.forEach(v => {
    (ausencias[String(v.user_id)] = ausencias[String(v.user_id)] || [])
      .push([String(v.fecha).slice(0,10), String(v.fecha_fin || v.fecha).slice(0,10)]);
  });
  const ausente = (uid, lunes) => {
    const domingo = sumarDias(lunes, 6);
    return (ausencias[String(uid)] || []).some(([i, f]) => i <= domingo && f >= lunes);
  };

  const carga = {};
  ids.forEach(id => { carga[id] = { semanas: 0, festivos: 0 }; });

  // La rotación arranca detrás de quien llevaba la última semana previa al rango
  let ultimo = previo ? ids.indexOf(String(previo)) : -1;
  const dist = id => (ids.indexOf(id) - ultimo - 1 + ids.length) % ids.length;

  const asignaciones = [];
  let respetadas = 0, saltadas = 0, sinCubrir = 0;

  for (const semana of semanas) {
    const yaAsignada = asignadas[semana];
    if (yaAsignada && !sobrescribir) {
      respetadas++;
      // Cuenta para el equilibrio aunque no se toque: si no, el reparto del
      // resto del año compensaría una carga que en realidad ya existe.
      const k = String(yaAsignada);
      if (carga[k]) {
        carga[k].semanas++;
        carga[k].festivos += festivosDeTramo(semana, festivos).length;
        ultimo = ids.indexOf(k);
      }
      continue;
    }

    const nFestivos = festivosDeTramo(semana, festivos).length;
    const libres = ids.filter(id => !ausente(id, semana));
    if (libres.length < ids.length) saltadas += ids.length - libres.length;

    // Nadie disponible: se asigna igual al siguiente del turno. Dejar la semana
    // vacía sería peor, porque nadie repararía en que no está cubierta.
    const pool = libres.length ? libres : ids;
    if (!libres.length) sinCubrir++;

    const elegido = [...pool].sort((a, b) => {
      if (equilibrarFestivos && nFestivos > 0) {
        // Semana con festivo: se la lleva quien menos festivos acumule
        return (carga[a].festivos - carga[b].festivos)
            || (carga[a].semanas  - carga[b].semanas)
            || (dist(a) - dist(b));
      }
      // Semana normal: quien menos semanas lleve. A igualdad, quien más festivos
      // acumule, para que sus semanas restantes tiendan a ser de las tranquilas.
      return (carga[a].semanas  - carga[b].semanas)
          || (carga[b].festivos - carga[a].festivos)
          || (dist(a) - dist(b));
    })[0];

    carga[elegido].semanas++;
    carga[elegido].festivos += nFestivos;
    ultimo = ids.indexOf(elegido);
    asignaciones.push({ semana, user_id: elegido });
  }

  return { asignaciones, respetadas, saltadas, sinCubrir, carga };
}

// Días de un periodo fecha..fecha_fin (ambos inclusive), como array de ISO
export const diasDelPeriodo = (fecha, fechaFin) => {
  const ini = String(fecha || "").slice(0,10);
  if (!ini) return [];
  const fin = fechaFin ? String(fechaFin).slice(0,10) : ini;
  const out = [];
  let cur = ini;
  // Tope de seguridad: un rango absurdo no debe colgar la interfaz
  for (let i = 0; cur <= fin && i < 400; i++) { out.push(cur); cur = sumarDias(cur, 1); }
  return out;
};

// Color estable por persona: depende solo del id, así que no cambia al filtrar
// ni al reordenar la lista de usuarios.
//
// No se usa una paleta fija: con pocas personas, los índices consecutivos caían
// en tonos vecinos y salían tres verdes seguidos. En vez de eso se recorre el
// círculo cromático con el ángulo áureo (137,5°), que deja a cada persona lo más
// lejos posible de la anterior por muchas que haya. Además se alternan tres
// bandas de saturación y luminosidad, para que dos tonos parecidos nunca
// coincidan también en intensidad.
//
// La luminosidad se mantiene entre 36% y 52%: las celdas del calendario llevan
// texto blanco encima y por encima de ahí deja de leerse.
// Vacaciones: solo las APROBADAS por RRHH en Odoo eximen horas. Las que esperan
// validacion se ven en el calendario y cuentan para el reparto de guardias, pero
// no liberan de fichar. Sin `estado` (filas anteriores a la migracion 012) se
// dan por aprobadas, que es como se comportaban.
export const vacacionAprobada = v => (v?.estado || "aprobada") === "aprobada";

// Cuanto se rellena el color de un periodo segun lo firme que sea. La
// progresion se lee sola: reservado -> pedido -> firme.
export const ALFA_ESTADO = { borrador: .10, solicitada: .28, rechazada: .10, aprobada: 1 };

// hsl(...) -> hsla(..., a). colorUsuario devuelve siempre hsl, y esto permite
// pintar translucido lo que aun no esta aprobado sin tocar la paleta.
export const conAlfa = (color, a) =>
  /^hsl\(/i.test(color) ? color.replace(/^hsl\(/i, "hsla(").replace(/\)\s*$/, `, ${a})`) : color;

export const colorUsuario = (userId, users=[]) => {
  const ids = users.map(u => String(u.id)).sort();
  const i = Math.max(0, ids.indexOf(String(userId)));
  const h = Math.round((i * 137.508) % 360);
  const banda = i % 3;
  const s = [70, 58, 85][banda];
  const l = [45, 36, 52][banda];
  return `hsl(${h}, ${s}%, ${l}%)`;
};

// Parse "2h", "1.5h", "30m", "45m", "2" (=hours) → hours as float
export const parseTimeInput = (val) => {
  if(val===null||val===undefined||val==="") return 0;
  const s = String(val).trim().toLowerCase();
  const m = s.match(/^([\d.]+)\s*([mh]?)$/);
  if(!m) { const n=parseFloat(s); return isNaN(n)?0:n; }
  const num = parseFloat(m[1])||0;
  return m[2]==="m" ? num/60 : num;
};
// Format hours → "30m" if <60 min, else "1.5h"
export const fmtHours = (h) => {
  const v = parseFloat(h)||0;
  const mins = Math.round(v*60);
  if(mins===0) return "0m";
  if(mins < 60) return `${mins}m`;
  return `${parseFloat(v.toFixed(1))}h`;
};
export const pct      = ts => ts.length ? Math.round(ts.filter(t=>t.status==="Completada").length/ts.length*100) : 0;


export const ringClr = p => p>=90?"#22c55e":p>=75?"#0b7d96":p>=50?"#f59e0b":p>=25?"#f97316":"#ef4444";

// ── Permisos ──────────────────────────────────────────────────────────────────
// Los roles en un sitio (espejo de backend/lib/roles.js): nadie compara role==="admin" a mano, asi un cambio de roles se hace
// cambiando solo estas lineas. Un nombre por funcion: esAdmin, esGestor, esSuper y nombreRol.
export const esAdmin          = u => u?.role==="admin"||u?.role==="super";
export const esGestor         = u => esAdmin(u)||u?.role==="gestor";
// Nombres de equipo comparables: sin acentos ni espacios de sobra.
const normEquipo = s => String(s||"").normalize("NFD").replace(/[̀-ͯ]/g,"").trim().toUpperCase();

// Equipos a los que pertenece alguien. `members` de /api/teams son ids en texto.
export const equiposDe = (u,teams=[]) =>
  u ? teams.filter(t => (t.members||[]).some(m => String(m)===String(u.id))) : [];
export const esDeEquipo = (u,teams,nombre) =>
  equiposDe(u,teams).some(t => normEquipo(t.name)===normEquipo(nombre));
export const esTIC = (u,teams) => esDeEquipo(u,teams,EQUIPO_TIC);
// «Super usuario» (espejo de esSuper en backend/lib/permisos.js), que levanta los limites de equipo. Solo el rol `super` (lote
// S3b): estar en TIC ya no hace super a un admin. Quien la llama aun pasa `teams`; se ignora.
export const esSuper = u => u?.role==="super";
// Nombre visible de cada rol (el pulido de etiquetas y colores es de diseno)
// Jerarquia de roles (espejo de puedeConcederRol en backend/lib/roles.js): solo admin y super conceden, y solo roles inferiores al
// suyo; el super, cualquiera.
const RANGO_ROL = { member: 0, gestor: 1, admin: 2, super: 3 };
export const puedeConcederRol = (u, r) => esSuper(u) || (esAdmin(u) && RANGO_ROL[r] !== undefined && RANGO_ROL[r] < RANGO_ROL[u.role]);
export const rolesConcedibles = u => ["member", "gestor", "admin", "super"].filter(r => puedeConcederRol(u, r));
export const nombreRol = r => r==="super"?"Super usuario":r==="admin"?"Administrador":r==="gestor"?"Gestor":"Usuario";

// El equipo "de casa" de alguien: el primero al que pertenece. Es lo que se
// propone por defecto donde antes se daba TIC por hecho — quien no tenga
// equipo se queda sin propuesta, que es mejor que ponerle la de otro.
export const equipoPrincipal = (u,teams=[]) => equiposDe(u,teams)[0]?.name || "";

// Lo que hoy es propio de sistemas. Esconder el menu es solo cortesia: quien
// corta de verdad es backend/lib/permisos.js, que devuelve 403.
export const puedeVerIncidencias = (u,teams) => esAdmin(u) || esTIC(u,teams);
// «Mis tareas» sale de forma gradual: de momento solo quien es admin Y esta en TIC (espejo de
// puedeVerMisTareas en backend/lib/permisos.js, que responde 403 al resto: si divergen, el
// menu se ve y la pagina falla). Para abrirla a todos hay que cambiar las dos funciones.
// Abierta a todos por defecto. El interruptor es MIS_TAREAS_ABIERTO en el backend y llega en el usuario (mis_tareas_abierto, de
// /auth/me y del login): NO es una constante compilada, asi se cierra sin deploy. Si el dato no llega (un servidor sin el
// campo) se asume CERRADO: el estado seguro es el de antes (admin + TIC).
export const puedeVerMisTareas = (u,teams) => u?.mis_tareas_abierto === true || esSuper(u,teams);
// Mandar una tarea a otra persona (encargos de Mis tareas). Espejo de puedeMandarA en backend/lib/permisos.js: admin y
// gestor, solo a miembros de sus propios equipos y nunca a si mismos. Solo sirve para no ofrecer a quien el servidor va a
// rechazar con un 403. Cuando exista el rol super usuario se cambia aqui y alli.
const miembrosDe = t => (t?.members || []).map(String);
// Con el interruptor CERRADO (mis_tareas_abierto distinto de true) emisor y receptor tienen que ver Mis tareas, que es admin
// + TIC: espejo de `piloto` en backend/lib/permisos.js. Abierto, no se exige. Aqui `receptor` tiene que ser el usuario entero
// (con su rol), no solo su id.
export const puedeMandarA = (emisor, receptor, teams) => {
  const e = String(emisor?.id ?? ""), r = String(receptor?.id ?? receptor ?? "");
  if (!e || !r || e === r || !esGestor(emisor)) return false;
  if (emisor?.mis_tareas_abierto !== true && !(esSuper(emisor, teams) && esSuper(receptor, teams))) return false;
  // T-125: un super manda a cualquiera. El cliente no sabe quien esta de baja: el servidor lo rechaza.
  if (esSuper(emisor, teams)) return true;
  return (teams || []).some(t => miembrosDe(t).includes(e) && miembrosDe(t).includes(r));
};
// Proponer una tarea con aprobacion (T-135). Espejo de backend/lib/propuestas.js (que la prueba pruebas/propuestas-permisos.mjs
// compara pareja a pareja): un member propone a si mismo o a alguien de un equipo suyo y la aprueba un gestor o admin del
// equipo del receptor (sin equipo, un admin; nunca quien la propone). Admin y gestor mandan directo con puedeMandarA.
// `equipos`: [{ members: [ids] }]; `usuarios`: las personas a considerar como aprobadoras ({ id, role, active? }).
const idsDe = xs => (xs || []).map(String);
const deOficio = esGestor;
const equiposConjuntos = (a, b, equipos) => (equipos || []).some(t => idsDe(t.members).includes(String(a)) && idsDe(t.members).includes(String(b)));
export const puedeProponerA = (emisor, receptor, equipos) => {
  const e = String(emisor?.id ?? ""), r = String(receptor?.id ?? receptor ?? "");
  if (!e || !r || !emisor || deOficio(emisor)) return false;
  if (typeof receptor === "object" && receptor !== null && receptor.active === false) return false;
  return e === r || equiposConjuntos(e, r, equipos);
};
export const aprobadoresDe = (receptor, usuarios, equipos, excluir) => {
  const r = String(receptor?.id ?? receptor ?? "");
  const fuera = excluir === undefined || excluir === null ? null : String(excluir?.id ?? excluir);
  const candidatos = (usuarios || []).filter(u => deOficio(u) && u.active !== false && String(u.id) !== fuera);
  const suyos = (equipos || []).filter(t => idsDe(t.members).includes(r));
  if (!suyos.length) return candidatos.filter(u => esAdmin(u)).map(u => String(u.id));
  const miembros = new Set(suyos.flatMap(t => idsDe(t.members)));
  return candidatos.filter(u => miembros.has(String(u.id))).map(u => String(u.id));
};
// Las guardias las cubre TIC; un gestor las consulta para planificar su equipo.
export const puedeVerGuardias    = (u,teams) => esGestor(u) || esTIC(u,teams);
// Editarlas (lote S6, P5): el super o un admin del equipo TIC. Regla aislada, espejo de puedeEditarGuardias en lib/permisos.js.
export const puedeEditarGuardias = (u,teams) => esSuper(u) || (esAdmin(u) && esTIC(u,teams));
// Equipos y usuarios: el gestor los ve; editarlos sigue siendo del admin.
// Crear, editar y borrar EQUIPOS es solo de un admin del equipo TIC (el servidor lo exige
// desde la auditoria de seguridad: quien decide quien esta en TIC levanta todos los limites).
// Espejo de soloSistemas en backend/routes/teams.js: aqui solo sirve para no ofrecer botones
// que el servidor va a rechazar con un 403.
export const puedeGestionarEquipos = (u,teams) => esSuper(u,teams);
// Lote S6: cambiar un equipo (nombre y color) tambien lo puede el admin que pertenece a el. Espejo de puedeEditarEquipo.
export const puedeEditarEquipo = (u, team) => esSuper(u) || (esAdmin(u) && miembrosDe(team).includes(String(u.id)));
export const puedeVerUsuarios    = u => esGestor(u);
// La lista de usuarios (lote S6, P1; espejo de GET /users): admin y super, toda la plantilla; el gestor, su gente.
export const usuariosVisiblesPor = (u, users = [], teams = []) =>
  esAdmin(u) ? users : esGestor(u) ? deSuGente(u, users, teams) : [];
export const puedeEditarUsuarios = u => esAdmin(u);
// Lote S6: el admin ve a toda la plantilla pero solo modifica a su gente; el super, a cualquiera. Espejo de puedeGestionarUsuario.
// A otro admin o a un super solo los toca un super (espejo de exigeSistemasSiEsAdmin en routes/users.js); a uno mismo, si.
export const puedeEditarUsuario = (u, objetivo, teams = []) => {
  if (esSuper(u)) return true;
  if (!esAdmin(u)) return false;
  const id = String(objetivo?.id ?? objetivo);
  if (id !== String(u.id) && typeof objetivo === "object" && esAdmin(objetivo)) return false;
  return genteDe(u, teams).has(id);
};

// «Su gente» (espejo de genteDe en backend/lib/permisos.js): ids, en texto, de uno mismo y de los miembros de sus equipos.
export const genteDe = (u, teams = []) => {
  const mios = new Set(u ? [String(u.id)] : []);
  equiposDe(u, teams).forEach(t => (t.members || []).forEach(m => mios.add(String(m))));
  return mios;
};
const deSuGente = (u, users, teams) => { const g = genteDe(u, teams); return users.filter(x => g.has(String(x.id))); };

// A quien puede uno gestionarle ausencias: a si mismo siempre, a su gente si es
// admin, y a toda la plantilla si es super. Espejo de
// puedeGestionarAusencias() en backend/lib/permisos.js — aqui solo sirve para no
// ofrecer en el desplegable gente que el servidor va a rechazar.
export const gestionablesPor = (u, users = [], teams = []) => {
  if (!u) return [];
  if (esSuper(u)) return users;
  if (!esAdmin(u)) return users.filter(x => String(x.id) === String(u.id));
  return deSuGente(u, users, teams);
};

// Cuadernos a los que se puede entrar (espejo de ambitoCuaderno en backend/lib/permisos.js):
// el super, todos; el admin y el gestor, los de su gente (el gestor, en solo lectura);
// el resto, solo el suyo. El servidor responde 403 a lo demas.
export const puedeVerCuadernoAjeno = u => esGestor(u);
export const cuadernosVisiblesPor = (u, users = [], teams = []) => {
  if (!u) return [];
  if (esSuper(u)) return users;
  if (!esGestor(u)) return users.filter(x => String(x.id) === String(u.id));
  return deSuGente(u, users, teams);
};

// Tocar (espejo de ambitoAsistencias().tocar): el super, a todos; el admin, a su gente; nadie mas.
export const puedeTocarAsistencia = (u,teams,userId) => {
  if (esSuper(u)) return true;
  if (!esAdmin(u)) return false;
  return genteDe(u, teams).has(String(userId));
};





// Todos llevan texto blanco encima (cabeceras del Cuaderno), asi que cada uno
// pasa 4,5:1 con blanco. colorConBlanco() fuerza lo mismo en los colores libres.
export const TIPO_COLORS = [
  "#0b7d96","#1565C0","#2563eb","#0f766e",
  "#2e7d32","#15803d","#a16207","#c2410c",
  "#C62828","#be185d","#6a1b9a","#7c3aed",
  "#4e342e","#92400e","#0e7490","#6b7280",
];

// Razon de contraste de un color con el blanco (1 a 21).
export const contrasteConBlanco = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return 21;
  const n = parseInt(m[1], 16);
  const lin = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  const l = 0.2126 * lin(n >> 16) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return 1.05 / (l + 0.05);
};

// Oscurece un color, sin cambiarle el tono, hasta que el texto blanco encima
// llegue a min:1 (AA = 4,5). Si ya llega, lo devuelve igual.
export const colorConBlanco = (hex, min = 4.5) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const rgb = [n >> 16, (n >> 8) & 255, n & 255];
  const aHex = (f) => "#" + rgb.map(v => Math.round(v * f).toString(16).padStart(2, "0")).join("");
  for (let f = 1; f > 0; f -= 0.02) {
    const c = aHex(f);
    if (contrasteConBlanco(c) >= min) return c;
  }
  return "#000000";
};
export const getTipoColor = (tipo, agTipos=[], tiposData=[]) => {
  const stored = tiposData.find(t => t.name === tipo);
  if (stored?.color) return stored.color;
  const idx = agTipos.indexOf(tipo);
  return TIPO_COLORS[idx >= 0 ? idx % TIPO_COLORS.length : 0];
};

// Los tipos sin icono siguen mostrando el punto de color, así que devuelve null
export const getTipoIcon = (tipo, tiposData=[]) =>
  tiposData.find(t => t.name === tipo)?.icon || null;

// ── Productividad ─────────────────────────────────────────────────────────────
// El peso lo fija un admin y es común a toda la empresa, por eso se resuelve por
// NOMBRE de tipo: así el índice de una persona se puede calcular con los pesos
// que haya cargados, sin depender de los ids de su cuaderno.
export const getTipoProd = (tipo, tiposData=[]) => {
  const stored = tiposData.find(t => t.name === tipo);
  if (stored && stored.productividad !== null && stored.productividad !== undefined)
    return Math.max(0, Math.min(100, Number(stored.productividad)));
  if (AG_PROD_SEED[tipo] !== undefined) return AG_PROD_SEED[tipo];
  return AG_PROD_DEFAULT;
};

export const prodBand = p => AG_PROD_BANDS.find(b => p >= b.min) || AG_PROD_BANDS[AG_PROD_BANDS.length - 1];

// Índice de productividad de un conjunto de entradas: media de los pesos
// ponderada por horas (0-100). Las entradas SIN tipo no se pueden ponderar, así
// que quedan fuera del índice y se devuelven aparte (sinTipo) para que la UI
// avise en vez de inflar o hundir el porcentaje sin decir por qué.
export const prodStats = (entries=[], tiposData=[]) => {
  let horas = 0, pond = 0, alta = 0, media = 0, baja = 0, sinTipo = 0;
  entries.forEach(a => {
    const h = parseFloat(a.hours) || 0;
    if (!h) return;
    if (!a.type) { sinTipo += h; return; }
    const p = getTipoProd(a.type, tiposData);
    horas += h;
    pond  += h * p / 100;
    if      (p >= 70) alta  += h;
    else if (p >= 40) media += h;
    else              baja  += h;
  });
  return {
    horas, alta, media, baja, sinTipo,
    // null (no 0) cuando no hay ni una hora con tipo: un 0 se leería como
    // "trabajo improductivo" en vez de "todavía no hay datos".
    index: horas ? Math.round(pond / horas * 100) : null,
    // Relación de dedicación: horas de alto valor por cada hora de bajo valor.
    // Infinity = solo hay horas de alto valor; null = no hay ninguna de las dos.
    ratio: baja > 0 ? alta / baja : (alta > 0 ? Infinity : null),
  };
};

// "2.4:1" — horas de productividad alta por cada hora de productividad baja
export const fmtRatio = r =>
  r === null || r === undefined ? "—" : (r === Infinity ? "sin horas de bajo valor" : `${r.toFixed(1)}:1`);

// Iconos disponibles para un tipo — solo nombres presentes en ICONS (constants.js)
export const TIPO_ICONS = [
  "lightning-fill","star-fill","flag","bookmark-check-fill","check2-square",
  "list-ul","kanban-fill","clipboard","journal-text","card-text",
  "people-fill","people","person-badge","chat-dots-fill","headset",
  "megaphone-fill","calendar3","clock","mortarboard-fill","lightbulb-fill",
  "exclamation-triangle-fill","sign-stop-fill","pause-circle-fill","shield-check","key",
  "code-slash","cpu-fill","tools","gear","hdd-network-fill",
  "file-text","folder2-open","files","search","graph-up-arrow",
  "bar-chart-fill","pie-chart-fill","currency-euro","cart-fill","truck",
  "boxes","building","bank","globe","briefcase-fill",
  "heart-pulse-fill","rocket-takeoff-fill","palette-fill","camera-fill","funnel-fill",
  "inbox","bell","house-fill","grid-3x3-gap-fill","person-check-fill",
];

// Uso real de cada tipo a partir de las entradas cargadas del cuaderno.
// Devuelve { [nombre]: {count, hours, first, last} } — el nombre es el vínculo real.
export const tiposUsageMap = (entries=[]) => {
  const map = {};
  entries.forEach(a => {
    const key = a.type || "";
    if (!key) return;
    const d = (a.entry_date || a.start_date || "").slice(0,10);
    const u = map[key] || (map[key] = { count:0, hours:0, first:null, last:null });
    u.count += 1;
    u.hours += parseFloat(a.hours) || 0;
    if (d) {
      if (!u.first || d < u.first) u.first = d;
      if (!u.last  || d > u.last ) u.last  = d;
    }
  });
  return map;
};
// ── Etapas del helpdesk ───────────────────────────────────────────────────────
// Las etapas vienen de Odoo en inglés. Se traducen SOLO para mostrar: el
// filtrado sigue usando el nombre original, así que una etapa nueva del
// proveedor aparece tal cual en vez de romper nada.
const ETAPAS_ES = {
  "new": "Nuevas", "awaiting": "En espera", "awaiting info": "Esperando información",
  "in progress": "En curso", "in-progress": "En curso", "progress": "En curso",
  "pending": "Pendientes", "on hold": "En pausa", "assigned": "Asignadas",
  "done": "Resueltas", "solved": "Resueltas", "resolved": "Resueltas",
  "closed": "Cerradas", "cancelled": "Canceladas", "canceled": "Canceladas",
  "rejected": "Rechazadas", "to do": "Por hacer", "todo": "Por hacer",
};
export const etapaES = n => ETAPAS_ES[String(n || "").trim().toLowerCase()] || n || "—";

// ── Colores de las etapas del helpdesk ────────────────────────────────────────
// En helpdesk_mgmt los nombres de etapa los configura cada instalación, así que
// el color se deduce del texto y nunca de ids, que el proveedor puede cambiar.
// El salto cromatico es el que se lee de un vistazo en el tablero: azul lo que
// entra, ambar lo que se esta trabajando, verde lo que acaba bien y rojo lo que
// acaba sin resolverse (cerrada, cancelada o rechazada). Cerrada va con las
// rojas a proposito: en helpdesk_mgmt es el cajon de lo que se archiva sin
// solucion, y confundirla con resuelta falseaba el recuento.
const ETAPA_SEMANTICA = [
  [/new|nuev/i,                              "#3b82f6"],  // azul
  [/progres|curso|asign|working/i,           "#f59e0b"],  // ámbar
  [/espera|awaiting|hold|pausa|pending/i,    "#06b6d4"],  // cian
  [/cola|queue|backlog|tarea|to ?do|hacer/i, "#8b5cf6"],  // violeta
  [/done|resuel|soluci/i,                    "#22c55e"],  // verde
  [/cerrad|closed|cancel|descart|reject|rechaz/i, "#ef4444"],  // rojo
];

// La etapa a la que mover una incidencia cuando alguien empieza a trabajarla.
//
// Se deduce del NOMBRE con el mismo patron que pinta de ambar el tablero —lo que
// se esta trabajando— porque en helpdesk_mgmt las etapas las configura cada
// instalacion: cablear un id se rompe en cuanto el proveedor añade una. De las
// que casen gana la primera por `sequence`, que es el orden del tablero.
//
// Devuelve null si ninguna casa, y entonces la etapa NO se toca: mover una
// incidencia a un sitio adivinado es peor que dejarla donde estaba.
const RE_EN_CURSO = /progres|curso|asign|working/i;
export const etapaEnCurso = (etapas = []) => etapas
  .filter(e => RE_EN_CURSO.test(e.name ?? e.nombre ?? ""))
  .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))[0] || null;

// La etapa a la que mover una incidencia que se acaba de resolver. Mismo
// criterio que la de arriba: se deduce del NOMBRE, gana la primera por
// `sequence` y si ninguna casa se devuelve null y la etapa no se toca.
//
// CERRADA y CANCELADA quedan fuera a proposito, igual que en el color del
// tablero: en helpdesk_mgmt son el cajon de lo que se archiva SIN solucion, y
// mandar alli lo resuelto falsearia el recuento.
const RE_RESUELTA = /done|resuel|soluci|solved/i;
export const etapaResuelta = (etapas = []) => etapas
  .filter(e => RE_RESUELTA.test(e.name ?? e.nombre ?? ""))
  .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))[0] || null;

// Para las etapas que no casen con nada. Se reparten por orden y se descartan
// las que ya estén ocupadas: así dos etapas nunca acaban del mismo color.
const ETAPA_RESERVA = ["#ec4899", "#14b8a6", "#a855f7", "#0ea5e9", "#84cc16", "#f97316", "#64748b"];

export const colorEtapa = nombre => {
  const par = ETAPA_SEMANTICA.find(([re]) => re.test(nombre || ""));
  return par ? par[1] : null;
};

// Mapa {idEtapa: color} calculado sobre la lista completa, que es lo que
// permite garantizar que no se repitan.
export function mapaColoresEtapas(etapas = []) {
  const mapa = {};
  const usados = new Set();

  etapas.forEach(e => {
    const c = colorEtapa(e.name ?? e.nombre);
    if (c) { mapa[e.id] = c; usados.add(c); }
  });

  let i = 0;
  etapas.forEach(e => {
    if (mapa[e.id]) return;
    while (i < ETAPA_RESERVA.length && usados.has(ETAPA_RESERVA[i])) i++;
    const c = ETAPA_RESERVA[i] || "#8b5cf6";
    mapa[e.id] = c; usados.add(c); i++;
  });

  return mapa;
}

// ── Sonido de aviso ───────────────────────────────────────────────────────────
// Se sintetiza con la Web Audio API en vez de cargar un fichero: no hay recurso
// que servir ni que cachear, y suena igual sin conexión.
const CLAVE_SONIDO = "alm_avisos_sonido";

export const sonidoActivo   = () => localStorage.getItem(CLAVE_SONIDO) !== "0";
export const alternarSonido = () => {
  const nuevo = !sonidoActivo();
  localStorage.setItem(CLAVE_SONIDO, nuevo ? "1" : "0");
  return nuevo;
};

let _audio = null;

export function reproducirAviso() {
  if (!sonidoActivo()) return;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!_audio) _audio = new AC();
    // Los navegadores dejan el contexto suspendido hasta que hay interacción
    // del usuario; al llegar aquí ya la ha habido, pero puede seguir dormido.
    if (_audio.state === "suspended") _audio.resume();

    const ahora = _audio.currentTime;
    [[880, 0], [1174.7, 0.13]].forEach(([hz, retardo]) => {
      const osc = _audio.createOscillator();
      const vol = _audio.createGain();
      osc.type = "sine";
      osc.frequency.value = hz;
      // Envolvente suave: un corte seco produce un chasquido audible.
      vol.gain.setValueAtTime(0.0001, ahora + retardo);
      vol.gain.exponentialRampToValueAtTime(0.14, ahora + retardo + 0.02);
      vol.gain.exponentialRampToValueAtTime(0.0001, ahora + retardo + 0.16);
      osc.connect(vol); vol.connect(_audio.destination);
      osc.start(ahora + retardo);
      osc.stop(ahora + retardo + 0.18);
    });
  } catch { /* sin audio disponible, el aviso visual basta */ }
}

// ── Ubicación del navegador ───────────────────────────────────────────────────
// Para el fichaje: si no se le mandan coordenadas, Odoo geolocaliza por la IP de
// quien hace la petición — que es el servidor de ALM, no el empleado — y deja
// todos los fichajes clavados en el centro de datos.
//
// El problema real es el tiempo: con precisión alta, un GPS que arranca en frío
// dentro de una nave tarda entre 10 y 30 segundos en dar el primer punto, y
// fichar no puede esperar tanto. Por eso la posición se pide POR ADELANTADO y se
// guarda; al pulsar, ya está lista.
let _ubicacion = null;   // { latitude, longitude, accuracy, ts }
let _vigilante = null;   // watchPosition en marcha, si lo hay

// Un punto solo vale si es reciente Y preciso.
//
// Las dos condiciones salieron de fichajes reales que caian a kilometros del
// sitio: en un PC sin GPS el navegador resuelve por wifi o por IP y devuelve un
// radio de varios kilometros tan campante, y un punto de hace tres minutos, que
// era lo que se daba por bueno, son varios kilometros si se viene conduciendo.
const VIGENCIA_MS   = 60 * 1000;   // un minuto de coche son ~1,5 km
const PRECISION_MAX = 1000;        // metros: por encima no es un sitio, es una comarca
const PRECISION_OK  = 100;         // metros: a partir de aqui ya no merece seguir esperando

const sirve = f => !!f && Date.now() - f.ts < VIGENCIA_MS && f.accuracy <= PRECISION_MAX;
const punto = f => ({ latitude: f.latitude, longitude: f.longitude,
                      precision: Math.round(f.accuracy) });

// Se queda con el MEJOR de los puntos frescos, no con el ultimo: el GPS empieza
// dando la celda o la wifi y va afinando, asi que el primero que llega casi
// nunca es el bueno.
const guardarFix = pos => {
  const f = { latitude: pos.coords.latitude, longitude: pos.coords.longitude,
              accuracy: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : 99999,
              ts: Date.now() };
  // Un punto de GPS recien llegado gana siempre: si el empleado se ha movido,
  // el guardado de hace medio minuto puede estar a un kilometro por muy fino
  // que fuera.
  if (!sirve(_ubicacion) || f.accuracy <= _ubicacion.accuracy || f.accuracy <= PRECISION_OK)
    _ubicacion = f;
};

// Con `id` solo para esa vigilancia: el temporizador del precalentado no debe
// cortar la que arranque despues un fichaje.
const pararVigilancia = (id = null) => {
  if (_vigilante === null || (id !== null && id !== _vigilante)) return;
  navigator.geolocation.clearWatch(_vigilante);
  _vigilante = null;
};

// Se llama al abrir ALM y al volver a la pestaña. No molesta al usuario: el
// permiso solo se pide una vez y después el navegador responde solo.
export function precalentarUbicacion() {
  if (!navigator.geolocation || _vigilante !== null) return;
  // Con un punto reciente y fino no hace falta volver a preguntar.
  if (sirve(_ubicacion) && _ubicacion.accuracy <= PRECISION_OK) return;
  // watchPosition y no getCurrentPosition: el navegador va entregando puntos
  // conforme el GPS afina y aqui interesa quedarse con el ultimo, el mas fino.
  // maximumAge en 0 para que no cuele uno guardado de antes de moverse.
  const id = navigator.geolocation.watchPosition(guardarFix, () => pararVigilancia(id),
    { enableHighAccuracy: true, timeout: 25000, maximumAge: 0 });
  _vigilante = id;
  setTimeout(() => pararVigilancia(id), 25000);
}

/**
 * Ubicación para adjuntar a un fichaje. Nunca falla ni bloquea: si no hay
 * permiso, no hay GPS o tarda demasiado, devuelve null y se ficha igual.
 *
 * Devuelve tambien la precision en metros. Viaja hasta el registro a proposito:
 * un punto de wifi con radio de 8 km y uno de GPS con radio de 10 m se pintaban
 * igual, y no habia forma de saber cual mirar.
 *
 * @param {number} msEspera  cuánto esperar si no hay ninguna posición buena
 */
export function obtenerUbicacion(msEspera = 8000) {
  if (sirve(_ubicacion)) return Promise.resolve(punto(_ubicacion));

  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve(null);

    let resuelto = false;
    const acabar = () => {
      if (resuelto) return;
      resuelto = true;
      pararVigilancia();
      // Se manda el mejor punto que haya aunque sea tosco: sin coordenadas,
      // Odoo geolocaliza la IP del servidor de ALM y clava el fichaje en el
      // centro de datos, que es peor. La precision va con el para que el
      // registro diga de que fiarse.
      resolve(_ubicacion && Date.now() - _ubicacion.ts < 5 * 60 * 1000 ? punto(_ubicacion) : null);
    };

    // Tope propio: el timeout de la API no cubre el tiempo que el usuario tarda
    // en contestar al permiso. Si ya hay un punto fresco aunque sea tosco solo
    // se espera un momento a ver si afina: en un PC sin GPS no va a mejorar
    // nunca y no se puede tener al empleado esperando en cada fichaje.
    const reloj = setTimeout(acabar,
      _ubicacion && Date.now() - _ubicacion.ts < VIGENCIA_MS ? 2500 : msEspera);

    // Si el precalentado seguia mirando, se reinicia aqui para no llevar dos
    // vigilancias a la vez.
    pararVigilancia();
    _vigilante = navigator.geolocation.watchPosition(
      pos => {
        guardarFix(pos);
        // En cuanto el punto es de fiar se corta: seguir esperando al GPS solo
        // retrasa el fichaje.
        if (sirve(_ubicacion) && _ubicacion.accuracy <= PRECISION_OK) { clearTimeout(reloj); acabar(); }
      },
      () => { clearTimeout(reloj); acabar(); },
      { enableHighAccuracy: true, timeout: msEspera, maximumAge: 0 }
    );
  });
}

// ── Progreso de la jornada ────────────────────────────────────────────────────
export const JORNADA_MS = 8 * 3600 * 1000;   // objetivo diario
export const LIMITE_MS  = 9 * 3600 * 1000;   // a partir de aqui, a casa

// Del rojo al verde segun se acerca a la jornada completa, y ambar al pasarse.
// Se interpola en HSL porque mezclar rojo y verde en RGB pasa por un marron
// sucio, mientras que en HSL recorre el naranja y el amarillo, que es justo la
// lectura que se busca.
export function colorJornada(ms) {
  if (ms >= LIMITE_MS) return "#f59e0b";
  const r = Math.max(0, Math.min(1, ms / JORNADA_MS));
  return `hsl(${Math.round(r * 142)}, 68%, 42%)`;
}

// Aviso de fin de jornada, una sola vez al dia y por usuario: el contador va
// corriendo cada segundo y sin memoria saltaria en cada tic.
const CLAVE_AVISO = (uid, dia) => `alm_aviso_jornada_${uid || "x"}_${dia}`;

export function avisoJornadaPendiente(uid, ms) {
  if (ms < LIMITE_MS) return false;
  const dia = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Madrid" }).format(new Date());
  const clave = CLAVE_AVISO(uid, dia);
  try {
    if (localStorage.getItem(clave)) return false;
    localStorage.setItem(clave, "1");
  } catch { /* sin localStorage se avisa una vez por sesion, que tampoco molesta */ }
  return true;
}

// ── Permisos del gestor de proyectos ──────────────────────────────────────────
// Espejo de ambitoProyectos() en backend/lib/permisos.js. Igual que el resto de
// esta seccion, esconder un boton aqui es solo cortesia: quien corta de verdad
// es el backend, que devuelve 403.
//
// Cuando la fila viene del servidor trae `can_edit` ya resuelto, y ESA es la
// verdad: se prefiere siempre al calculo local. El calculo local solo existe
// para filas que aun no han vuelto del servidor (creacion optimista).

// Crear proyectos es del admin. El gestor ve todo pero no reorganiza nada.
export const puedeCrearProyectos = u => esAdmin(u);

export const puedeEditarProyecto = (u, p, teams = []) => {
  if (!u || !p) return false;
  if (p.can_edit !== undefined) return !!p.can_edit;   // lo que dijo el servidor
  if (!esAdmin(u)) return false;
  if (esSuper(u, teams)) return true;
  const mios = equiposDe(u, teams).map(t => String(t.id));
  if (p.team_id && mios.includes(String(p.team_id))) return true;
  if (String(p.owner_id) === String(u.id)) return true;
  // "De su equipo" tambien por las personas: cubre los proyectos heredados, que
  // no tienen team_id. Mismo criterio que la rama equivalente del backend.
  const gente = new Set();
  equiposDe(u, teams).forEach(t => (t.members || []).forEach(m => gente.add(String(m))));
  return (p.miembros || []).some(m => gente.has(String(m.user_id)));
};

// "Mis tareas": estar asignado con cualquier papel. Es el permiso del member.
export const esMiTarea = (u, t) =>
  !!u && (t?.asignados || []).some(a => String(a.user_id) === String(u.id));

export const puedeEditarTarea = (u, t, p, teams = []) => {
  if (t?.can_edit !== undefined) return !!t.can_edit;
  if (puedeEditarProyecto(u, p, teams)) return true;
  return esMiTarea(u, t);
};

// Quien no puede reorganizar el proyecto solo avanza SU trabajo: no replanifica
// fechas, no reasigna y no mueve el paso. Esta lista tiene que decir lo mismo
// que CAMPOS_TAREA_PROPIOS en backend/lib/permisos.js.
export const CAMPOS_TAREA_PROPIOS = ["status","progreso","nota_bloqueo","description","estimacion_min"];

export const puedeEditarCampoTarea = (u, t, p, campo, teams = []) =>
  puedeEditarProyecto(u, p, teams)
    ? true
    : (esMiTarea(u, t) && CAMPOS_TAREA_PROPIOS.includes(campo));

// El desglose de horas por persona es dato de rendimiento individual, asi que se
// alinea con Asistencias: admin y gestor ven la plantilla, el resto solo su
// propia linea. El servidor ya lo recorta y marca la respuesta como `parcial`;
// esto sirve para explicarlo en la interfaz en vez de esconderlo.
export const puedeVerTiempoDeOtros = u => esGestor(u);

// ── Progreso de un proyecto ───────────────────────────────────────────────────
// Solo cuentan los pasos de trabajo: los nodos de control del diagrama (inicio,
// fin, condicional, hito) no son tareas y falsearian el porcentaje.
export const pasosDeTrabajo = (tareas = []) => tareas.filter(t => (t.kind || "tarea") === "tarea");

// Avance por pasos: el mismo criterio que pct(), pero ignorando los nodos de
// control. Devuelve null y no 0 cuando no hay pasos: un 0 se leeria como
// "nada hecho" en vez de "nada planificado".
export const avancePasos = (tareas = []) => {
  const t = pasosDeTrabajo(tareas);
  if (!t.length) return null;
  return Math.round(t.filter(x => x.status === "Completada").length / t.length * 100);
};

// ── El estado del proyecto de un vistazo ──────────────────────────────────────
// Un solo color que resume TODOS los pasos, para pintarlo en el nodo de inicio.
//
// El orden de precedencia no es arbitrario: el color tiene que decir lo que mas
// necesita tu atencion ahora mismo, no lo que mas abunda. Por eso un unico paso
// bloqueado tiñe el proyecto entero, aunque los otros veinte vayan bien: ese es
// justo el dato que no se puede perder en una media.
//
//   ¿hay algo roto?      -> rojo      un paso bloqueado
//   ¿hay algo moviendose? -> azul     alguien esta trabajando
//   ¿hay algo parado?    -> ambar     nada avanza y algo espera
//   ¿esta acabado?       -> verde     todo el trabajo cerrado
//   si no                -> gris      sin empezar, o abandonado
//
// "En progreso" gana a "En espera" a proposito: con cinco pasos en marcha y uno
// esperando, el proyecto va, y pintarlo de ambar exagera el problema. Solo
// cuando NADA se mueve, la espera pasa a ser la noticia.
//
// Se aparta de lo que se pidio en un punto: un proyecto entero cancelado sale
// GRIS y no rojo. El rojo es una llamada a actuar y en lo cancelado no hay nada
// que hacer; compitiendo con los bloqueos, el rojo dejaria de significar "ven
// aqui". Un abandonado es exactamente igual de inerte que uno sin empezar.
export const ESTADOS_PROYECTO_VISTA = {
  bloqueado:  { color: "#ef4444", label: "Hay trabajo bloqueado" },
  enCurso:    { color: "#0b7d96", label: "Hay trabajo en marcha" },
  enEspera:   { color: "#f59e0b", label: "Todo lo abierto esta esperando" },
  completado: { color: "#22c55e", label: "Todo el trabajo esta cerrado" },
  cancelado:  { color: "var(--text3)", label: "Todo el trabajo se ha cancelado" },
  sinEmpezar: { color: "var(--text3)", label: "Todavia no ha empezado nada" },
  vacio:      { color: "var(--text3)", label: "Este flujo no tiene pasos de trabajo" },
};

export function estadoProyecto(tareas = []) {
  const pasos = pasosDeTrabajo(tareas);
  const conteo = {};
  for (const t of pasos) conteo[t.status] = (conteo[t.status] || 0) + 1;
  const hay = e => (conteo[e] || 0) > 0;

  let clave;
  if (!pasos.length)                      clave = "vacio";
  else if (hay("Bloqueada"))              clave = "bloqueado";
  else if (hay("En progreso"))            clave = "enCurso";
  else if (hay("En Espera"))              clave = "enEspera";
  else if (hay("Completada")
        && !hay("Pendiente"))             clave = "completado";
  else if ((conteo.Cancelada || 0) === pasos.length) clave = "cancelado";
  else                                    clave = "sinEmpezar";

  return { clave, conteo, total: pasos.length, ...ESTADOS_PROYECTO_VISTA[clave] };
}

// Avance por esfuerzo: Σ min(real, estimado) / Σ estimado. El min() esta a
// proposito, y es la parte que importa: sin acotar, un paso desbocado (300 % de
// su estimacion) dispararia el avance del proyecto entero por encima de lo que
// de verdad se ha terminado. Devuelve null si nadie ha estimado nada.
export const avanceEsfuerzo = (tareas = [], minutosPorTarea = {}) => {
  let est = 0, hecho = 0;
  for (const t of pasosDeTrabajo(tareas)) {
    const e = Number(t.estimacion_min) || 0;
    if (!e) continue;
    est   += e;
    hecho += Math.min(Number(minutosPorTarea[String(t.id)]) || 0, e);
  }
  if (!est) return null;
  return Math.round(hecho / est * 100);
};
